import React, { useState } from 'react';
import { ApolloError } from '@apollo/client';

import { type Menu, Permission, type Venue } from 'generated/graphql';
import { AppsList } from 'components/AppsList/AppsList';
import ChangeCta from 'components/SingleMenu/ChangeCta';
import BackLink from 'components/BackLink/BackLink';
import Button from 'components/Button/Button';
import Image from 'components/Image/Image';
import { useVenueContext } from 'routes/Main';
import { useErrorModal } from 'utils';

import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import titleStyles from './Title.module.scss';
import styles from './MenuTitle.module.scss';

interface Props {
  menu: Menu;
  isLoading?: boolean;
  neverPublished: boolean;
  displayName?: string;
  themeClassName?: string | null;
  handlePublish?: () => void;
  handleSave?: () => Promise<{
    error: ApolloError;
    currentVenue: Venue | undefined;
  } | null>;
  isMobileView?: boolean;
  changesDetected?: boolean;
  busyUntil?: string | null;
}

const MenuTitle: React.FC<Props> = ({
  menu: { name, apps, imageUri, mmId },
  isLoading,
  neverPublished,
  displayName,
  themeClassName,
  handlePublish,
  handleSave,
  isMobileView,
  changesDetected,
  busyUntil
}) => {
  const { currentUser } = useVenueContext();
  const errorModalHandler = useErrorModal();
  const canEditItem = !!currentUser?.permissions?.includes(Permission.PublishMenu);
  const changesDetectedText = neverPublished ? 'Unpublished menu' : 'Unpublished changes';
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const onPublishButtonClick = async (): Promise<void> => {
    if (handlePublish) {
      const appToLog = apps[0].id.includes('virtual') && apps[0].brand ? `${apps[0].name} - brand` : apps[0].name;
      logAmplitudeEvent({
        name: 'click publish menu',
        category: AmplitudeEventCategories.MM_MENU,
        properties: {
          [AmplitudeEventProperties.MENU_ID]: mmId,
          [AmplitudeEventProperties.MENU_NAME]: name,
          [AmplitudeEventProperties.APP]: appToLog
        }
      });

      await handlePublish();
    }
  };

  const SaveButton = handleSave ? (
    <Button
      disabled={isLoading}
      id="save-menu-button"
      level="low"
      onClick={async () => {
        setIsLoadingSave(true);
        const error = await handleSave();
        setIsLoadingSave(false);
        if (error) {
          errorModalHandler(error.error, error.currentVenue);
        }
      }}
    >
      {isLoadingSave ? <i className="mm-circle-o-notch mm-spin" /> : 'Save'}
    </Button>
  ) : null;
  return (
    <section
      className={`${titleStyles.Outer} ${styles.Outer} ${themeClassName ? `Theme${themeClassName}` : ''}`}
      data-testid="menu-title"
    >
      <div className="container">
        <div>
          <div className={styles.BackLink}>
            <BackLink />
          </div>
          {isMobileView ? (
            <div className={styles.Content}>
              <div>
                {busyUntil && <p className={styles.Chip}>unavailable until {busyUntil}</p>}
                <h1 data-testid="menu-title-h1" className={isLoading ? 'LoadingCoverText' : ''}>
                  {displayName}
                </h1>
              </div>
              {(handlePublish || handleSave) && canEditItem ? (
                <div className={styles.Publish} data-testid="menu-title-publish">
                  {SaveButton}
                  {handlePublish && (
                    <Button onClick={onPublishButtonClick} id="publish-menu-button" level="high">
                      <span>Publish</span>
                    </Button>
                  )}
                </div>
              ) : null}
              {(changesDetected || neverPublished) && canEditItem ? (
                <div className={styles.UnpublishedChanges}>
                  <i className="Dot mixed" />
                  <span>{changesDetectedText}</span>
                </div>
              ) : null}
              {imageUri ? (
                <Image
                  isLoading={isLoading}
                  className={styles.Image}
                  testId="menu-title-image"
                  src={imageUri}
                  alt={name}
                />
              ) : null}
            </div>
          ) : (
            <div className={styles.Content}>
              {imageUri ? (
                <Image
                  isLoading={isLoading}
                  className={styles.Image}
                  testId="menu-title-image"
                  src={imageUri}
                  alt={name}
                />
              ) : null}
              <div>
                {busyUntil && (
                  <p data-testid="busy-until-chip" className={styles.Chip}>
                    unavailable until {busyUntil}
                  </p>
                )}
                <h1 data-testid="menu-title-h1" className={isLoading ? 'LoadingCoverText' : ''}>
                  {displayName}
                </h1>
                <AppsList isLoading={isLoading} disableShrinkOnMobile apps={apps} />
              </div>
            </div>
          )}
        </div>
        {(handlePublish || handleSave) && canEditItem && !isMobileView ? (
          <div className={styles.Publish} data-testid="menu-title-publish">
            {handlePublish && (
              <Button onClick={onPublishButtonClick} id="publish-menu-button" level="high">
                <span>Publish</span>
              </Button>
            )}
            {SaveButton}
            {(changesDetected || neverPublished) && (
              <div>
                <i className="Dot mixed" />
                <span>{changesDetectedText}</span>
              </div>
            )}
          </div>
        ) : null}
      </div>
      <div className="container">
        <ChangeCta />
      </div>
    </section>
  );
};

export default MenuTitle;
